import React, { useRef, useEffect, useState } from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
} from 'chart.js';
import { Line } from "react-chartjs-2";
import { selectColorByScore } from '../helpers/selectColor.js';
import { useWindowsSize } from '../helpers/userWindowSize.jsx';
import { Tooltip as TooltipMUI, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CachedIcon from '@mui/icons-material/Cached';
import InfoIcon from '@mui/icons-material/Info';
import DialogComponent from '../../provision-app/components/dialogComponent';
import '../styles.css';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const SentimentScoreHistory = ({ historyData, regenContactHistory }) => {
    //const skipped = (ctx, value) => ctx.p0.skip || ctx.p1.skip ? value : undefined;
    //const down = (ctx, value) => ctx.p0.parsed.y > ctx.p1.parsed.y ? value : undefined;
    const { innerHeight, innerWidth } = useWindowsSize();
    const chartRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    const selectFontSize = () => {
		const newFontSize = innerHeight * 0.008;
		return newFontSize > 8 ? (newFontSize < 20 ? newFontSize : 20) : 8;
	}
    
    const customPlugin = {
		id: 'customPlugin',
		// Highlight last XAxis element.
		beforeDraw: (chart) => {
			const categoryScale = chart.boxes[2];

			if (!categoryScale || !categoryScale.ticks || !categoryScale.ticks.length > 0) {
				return;
			}

			const length = categoryScale.ticks.length;
			categoryScale.ticks[length - 1].highlighted = true;
		}
	};

    useEffect(() => {
		// Force resizing to prevent blank chart.
		try {
			chartRef.current && chartRef.current.resize();
		} catch (err) {
			throw new Error(`An error occurred: ${err}`);
		}
	}, []);

    /**
	  * Returns a suitable height for each range.
	  *
	  * The ranges are:
	  * - width > 1000 and height < 300: 15% of total width
	  * - width > 1000 and height > 300: 50% of total width
	  * - width < 1000: 30% of total width
	  *
	  * all values in the range are exclusive
	*/
	const handleSelectHeight = () => {
		return innerWidth > 1000 ? (innerHeight < 300 ? '30%' : '100%') : '60%';
	}

    // Process sentimentData directly
    const data = historyData ? {
        labels: Array.isArray(historyData) ? historyData.map(item => new Date(item.dateCreated).toISOString().split('T')[0]) : [],
        datasets: [
            {
                label: 'Note Sentiment',
                data: Array.isArray(historyData) ? historyData.map(item => item.sentimentScore) : [],
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                segment: {
                    borderColor: ctx => ctx && selectColorByScore(ctx.p1.parsed.y)
                },
            },
        ],
    } : null;
   
    const footer = (tooltipItems) => {
        let contactName = '';
        if (tooltipItems.length > 0) {
            const index = tooltipItems[0].dataIndex;
            const item = historyData[index];
            contactName = item.contact.name; 
        }
        //return `Contact: ${item.contact.name}\nDate: ${item.dateCreated}\nSentiment Score: ${item.sentimentScore}`;
        return `Contact: ${contactName}`;
    };

    const options = {   
        responsive: true,
        layout: {
            padding: {
                bottom: 5,
                left: 20,
                right: innerWidth < 800 ? 60 : 80,
                top: 5,
            },
        },
        hover: {
            mode: 'nearest',
            intersect: true
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Contact Note Sentiment History',
                align: 'start',
                font: {
                    size: 18,
                },
            },
            tooltip: {
                callbacks: {
                  footer: footer,
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                },
                ticks: {
                    padding: 2,
                    font: {
                        size: selectFontSize(),
                        weight: (c) => c.tick && c.tick.highlighted ? 'bolder' : '',
                    },
                },
            },
            y: {
                grid: {
                    display: true,
                },
                ticks: {
                    padding: 10,
                    color: (c) => c.tick && selectColorByScore(c.tick.value),
                    font: {
                        size: selectFontSize(),
                        weight: 'bolder',
                    }
                },
                suggestedMin: 0,
                suggestedMax: 100,
            },
        },
        elements: {
            point: {
                radius: 5,
                backgroundColor: (c) => c.parsed && selectColorByScore(c.parsed.y),
                pointStyle: 'circle',
            }
        }
    }

    const handleClick = () => {
        setIsLoading(true);
        regenContactHistory();
    };

    const [showModal, setShowModal] = useState({
        open: false,
        title: '',
        description: '',
        children: null,
    });

    const handleOpenInfo = (html) => {
        const displayText = (
            <ul style={{margin: 0}}>
                <li>The Contact Sentiment Pod has two main sections:</li>
                <ul>
                    <li><strong>Overall Sentiment:</strong> Displays an overall sentiment score for all the contact's notes.
                        <ul>
                            <li>The overall sentiment score is cached in a custom field in the ticket.</li>
                            <li>The regenerate button allows you to regenerate the overall contact sentiment in real-time.</li>
                        </ul>
                    </li>
                    <li><strong>Contact Sentiment History:</strong> The chart displays the sentiment score of the contact notes over time.
                        <ul>
                            <li>The regenerate button allows you to regenerate the contact note sentiment history in real-time for 10 notes at a time.</li>
                        </ul>
                    </li>

                </ul>
            </ul>
        );
        setShowModal({
            open: true,
            title: 'Contact Sentiment Pod Info',
            description: displayText,
            children: '', 
        });
    }

    return (
        <>
        {historyData && (
            <section className="customer-sentiment__score-history">
                <Line ref={chartRef} data={data} options={options} plugins={[customPlugin]} height={handleSelectHeight()}/>
                <div className="row-container">
                    <div className="left-content">
                        <IconButton aria-label="Open Info" size="small">
                            <InfoIcon fontSize="medium" onClick={handleOpenInfo} color="primary"/>
                        </IconButton>  
                    </div>
                    <TooltipMUI title="Regenerate Contact Note Sentiment History">
                        <LoadingButton
                            style={{ marginRight: '20px' }}
                            size="small" 
                            onClick={handleClick} 
                            variant="outlined" 
                            endIcon={<CachedIcon />} 
                            loading={isLoading} 
                            loadingPosition="end">Regenerate History
                        </LoadingButton>
                    </TooltipMUI> 
                </div>
            </section>
        )}
        {showModal && (
                <DialogComponent
                    open={showModal.open}
                    handleClose={() => setShowModal({ ...showModal, open: false })}
                    title={showModal.title}
                    description={showModal.description}
                >
                    {showModal.children}
                </DialogComponent>
            )}              
        </> 
    );
}

export default SentimentScoreHistory;